import { apiGet, apiPost, ApiClientError } from './client';

/**
 * Phase 87 (Plan 87-08) — frontend API client for the D-07/D-08 usage read
 * surfaces built in Plan 87-07 (`backend/src/services/botUsageReadService.ts`,
 * `backend/src/api/schemas/botUsage.ts`). Field names mirror
 * `adminBotUsageListResponse` / `selfBotUsageResponse` EXACTLY —
 * fast-json-stringify strips anything not enumerated on the backend, so
 * there is nothing to type here beyond what the backend actually returns.
 */

/** One weekly slot of the admin-only trend sparkline (MKS-268), mirroring the
 * backend `weeklyTrend` array item schema member for member. */
export interface BotUsageWeeklyTrendPoint {
  weekStart: string;
  resetAt: string | null;
  peakPct: number | null;
}

/**
 * MKS-298 — one person's full Claude Code usage from the org-wide
 * OpenTelemetry export, mirroring the backend `claudeCode` sub-schema member
 * for member. `null` on a row means nothing has ever been reported for that
 * person: the UI shows "no Claude Code data", never `$0`.
 */
export interface ClaudeCodeUsage {
  costUsdToday: number;
  costUsd30d: number;
  tokens30d: number;
  reportedCostUsdToday: number;
  reportedCostUsd30d: number;
  lastReportAt: string | null;
  topModelId: string | null;
  tokensMain30d: number;
  tokensSubagent30d: number;
  tokensOther30d: number;
  /** MKS-311 — the same 30 days split by where the usage ran (Anthropic's cost). */
  bySource30d: Record<'own' | 'monka' | 'factory', { costUsd: number; tokens: number }>;
}

/** MKS-311 — "Own $X · Monka $Y · Factory $Z" for the tooltip and the profile card. */
export function formatUsageSourceSplit(split: ClaudeCodeUsage['bySource30d']): string {
  return (['own', 'monka', 'factory'] as const)
    .map((key) => `${key === 'own' ? 'Own' : key === 'monka' ? 'Monka' : 'Factory'} $${split[key].costUsd.toFixed(2)}`)
    .join(' · ');
}

/** MKS-298 — usage the per-connection table has no row for. */
export interface UnattributedClaudeCodeUsage {
  subjectKey: string;
  userId: number | null;
  displayName: string | null;
  reason: string;
  costUsd30d: number;
  tokens30d: number;
  lastReportAt: string | null;
}

/**
 * MKS-298 — the OTLP ingest token's STATUS. `hint` is the token's last 4
 * characters; the token itself only ever exists in the rotate response.
 */
export interface ClaudeCodeIngestTokenStatus {
  configured: boolean;
  hint: string | null;
  rotatedAt: string | null;
  rotatedByUserId: number | null;
  organizationId: string | null;
}

export interface ClaudeCodeIngestTokenRotation extends ClaudeCodeIngestTokenStatus {
  token: string;
}

export interface BotUsageAdminRow {
  connectionId: number;
  userId: number;
  slackUserId: string;
  displayName: string | null;
  email: string | null;
  isRobot: boolean;
  disabledByAdmin: boolean;
  sessionPct: number | null;
  sessionResetAt: string | null;
  sessionStatus: string | null;
  weeklyPct: number | null;
  weeklyResetAt: string | null;
  weeklyStatus: string | null;
  weeklyWindowKey: string | null;
  lastSource: string | null;
  lastCapturedAt: string | null;
  tokens30d: number;
  costUsd30d: number;
  costUsdToday: number;
  tokensLatestTurn: number;
  alertThresholdFired: number | null;
  seatTier: string | null;
  weeklyTrend: BotUsageWeeklyTrendPoint[];
  claudeCode: ClaudeCodeUsage | null;
}

export type BotUsageSelfRow = Omit<
  BotUsageAdminRow,
  'email' | 'slackUserId' | 'disabledByAdmin' | 'isRobot' | 'seatTier' | 'weeklyTrend'
>;

/**
 * Defense-in-depth against an unexpectedly-empty response body, mirroring
 * `botAdmin.ts`'s `assertNonEmpty` helper shape.
 */
function assertNonEmpty<T extends object>(res: T | undefined, what: string): T {
  if (res === undefined || res === null) {
    throw new ApiClientError({
      statusCode: 502,
      error: 'Empty Response',
      message: `Server returned an empty response${what ? ` (${what})` : ''}`,
    });
  }
  return res;
}

export async function getBotUsageAdmin(): Promise<BotUsageAdminRow[]> {
  const res = await apiGet<{ data: BotUsageAdminRow[] }>('/admin/bot/usage');
  return assertNonEmpty(res, 'bot usage admin list').data;
}

/**
 * `null` is a legitimate "never connected" answer for the self view, so it
 * must NOT go through `assertNonEmpty` — that guard exists for a truncated
 * 200 body, not a genuine `data: null`.
 */
export async function getMyBotUsage(): Promise<BotUsageSelfRow | null> {
  const res = await apiGet<{ data: BotUsageSelfRow | null }>('/bot/usage/me');
  return res?.data ?? null;
}

/**
 * MKS-298 — Claude Code usage the Usage tab's table cannot show (an email no
 * Monksheets user owns, or a matched user with no bot connection), so an
 * admin can see it instead of it being silently dropped.
 */
/** A Claude seat holder with no bot connection (no row in the Usage table). */
export interface UnconnectedSeatHolder {
  userId: number;
  displayName: string;
  email: string;
  tier: string | null;
}

/** Granted Claude seats per tier across all active users. */
export interface ClaudeSeatCounts {
  premium: number;
  standard: number;
  notConnected: UnconnectedSeatHolder[];
}

export async function getClaudeSeatCounts(): Promise<ClaudeSeatCounts> {
  const res = await apiGet<{ data: ClaudeSeatCounts }>('/admin/bot/claude-seats');
  return assertNonEmpty(res, 'Claude seat counts').data;
}

export async function getUnattributedClaudeCodeUsage(): Promise<UnattributedClaudeCodeUsage[]> {
  const res = await apiGet<{ data: UnattributedClaudeCodeUsage[] }>(
    '/admin/bot/claude-code-usage/unattributed',
  );
  return assertNonEmpty(res, 'unattributed Claude Code usage').data;
}

export async function getClaudeCodeIngestTokenStatus(): Promise<ClaudeCodeIngestTokenStatus> {
  const res = await apiGet<{ data: ClaudeCodeIngestTokenStatus }>(
    '/admin/bot/claude-code-usage/ingest-token',
  );
  return assertNonEmpty(res, 'Claude Code ingest token status').data;
}

/**
 * Rotate the OTLP ingest token. The plaintext in the response is the ONLY
 * copy — it cannot be read back, and every machine keeps exporting into a 401
 * until the claude.ai managed-settings payload is re-pasted with it.
 */
export async function rotateClaudeCodeIngestToken(): Promise<ClaudeCodeIngestTokenRotation> {
  const res = await apiPost<{ data: ClaudeCodeIngestTokenRotation }>(
    '/admin/bot/claude-code-usage/ingest-token/rotate',
    {},
  );
  return assertNonEmpty(res, 'Claude Code ingest token rotation').data;
}
